import { useState, useEffect } from 'react';
import api, { handleApiError } from '../services/api';
import toast from 'react-hot-toast';
import { Save, Settings, Building2 } from 'lucide-react';

export default function SettingsPage() {
  const [form, setForm] = useState({
    company_name: '',
    address: '',
    phone: '',
    email: '',
    tax_id: '',
    default_warehouse_id: '',
    currency: 'IDR',
    decimal_places: 0,
    allow_negative_stock: false,
    valuation_method: 'fifo',
  });
  const [warehouses, setWarehouses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchSettings();
    fetchWarehouses();
  }, []);

  const fetchSettings = async () => {
    try {
      const res = await api.get('/settings');
      if (res.data.success && res.data.data) {
        setForm({
          company_name: res.data.data.company_name || '',
          address: res.data.data.address || '',
          phone: res.data.data.phone || '',
          email: res.data.data.email || '',
          tax_id: res.data.data.tax_id || '',
          default_warehouse_id: res.data.data.default_warehouse_id || '',
          currency: res.data.data.currency || 'IDR',
          decimal_places: res.data.data.decimal_places ?? 0,
          allow_negative_stock: res.data.data.allow_negative_stock || false,
          valuation_method: res.data.data.valuation_method || 'fifo',
        });
      }
    } catch (error) {
      handleApiError(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchWarehouses = async () => {
    try {
      const res = await api.get('/warehouses');
      if (res.data.success) {
        setWarehouses(res.data.data || []);
      }
    } catch {
      // Silently fail
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await api.put('/settings', form);
      if (res.data.success) {
        toast.success('Pengaturan berhasil disimpan');
      }
    } catch (error) {
      handleApiError(error);
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div>
          <div className="skeleton h-8 w-48 mb-2" />
          <div className="skeleton h-4 w-64" />
        </div>
        <div className="card">
          <div className="card-body space-y-4">
            {[...Array(6)].map((_, i) => (
              <div key={i}>
                <div className="skeleton h-4 w-24 mb-2" />
                <div className="skeleton h-10 w-full" />
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="page-title">Pengaturan</h1>
        <p className="page-subtitle">Kelola pengaturan perusahaan dan preferensi sistem</p>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="card">
          <div className="card-header flex items-center">
            <Building2 className="h-5 w-5 mr-2 text-primary-500" />
            <h3 className="font-semibold text-gray-900 dark:text-white">Informasi Perusahaan</h3>
          </div>
          <div className="card-body space-y-5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <label className="label">Nama Perusahaan</label>
                <input
                  type="text"
                  className="input"
                  value={form.company_name}
                  onChange={(e) => handleChange('company_name', e.target.value)}
                  placeholder="Nama perusahaan"
                />
              </div>
              <div>
                <label className="label">NPWP / Tax ID</label>
                <input
                  type="text"
                  className="input"
                  value={form.tax_id}
                  onChange={(e) => handleChange('tax_id', e.target.value)}
                  placeholder="Nomor NPWP"
                />
              </div>
              <div className="md:col-span-2">
                <label className="label">Alamat</label>
                <textarea
                  className="input min-h-[80px]"
                  value={form.address}
                  onChange={(e) => handleChange('address', e.target.value)}
                  placeholder="Alamat lengkap perusahaan"
                  rows={3}
                />
              </div>
              <div>
                <label className="label">Telepon</label>
                <input
                  type="text"
                  className="input"
                  value={form.phone}
                  onChange={(e) => handleChange('phone', e.target.value)}
                  placeholder="Nomor telepon"
                />
              </div>
              <div>
                <label className="label">Email</label>
                <input
                  type="email"
                  className="input"
                  value={form.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  placeholder="Email perusahaan"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="card mt-6">
          <div className="card-header flex items-center">
            <Settings className="h-5 w-5 mr-2 text-primary-500" />
            <h3 className="font-semibold text-gray-900 dark:text-white">Preferensi Sistem</h3>
          </div>
          <div className="card-body space-y-5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <label className="label">Gudang Default</label>
                <select
                  className="input"
                  value={form.default_warehouse_id}
                  onChange={(e) => handleChange('default_warehouse_id', e.target.value)}
                >
                  <option value="">Pilih Gudang</option>
                  {warehouses.map((w) => (
                    <option key={w.id} value={w.id}>{w.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="label">Mata Uang</label>
                <select
                  className="input"
                  value={form.currency}
                  onChange={(e) => handleChange('currency', e.target.value)}
                >
                  <option value="IDR">IDR - Rupiah</option>
                  <option value="USD">USD - Dollar</option>
                  <option value="EUR">EUR - Euro</option>
                  <option value="SGD">SGD - Dollar Singapura</option>
                </select>
              </div>
              <div>
                <label className="label">Jumlah Desimal</label>
                <select
                  className="input"
                  value={form.decimal_places}
                  onChange={(e) => handleChange('decimal_places', Number(e.target.value))}
                >
                  <option value={0}>0 (tidak ada desimal)</option>
                  <option value={2}>2 (contoh: 1.000,00)</option>
                  <option value={3}>3 (contoh: 1.000,000)</option>
                </select>
              </div>
              <div>
                <label className="label">Metode Valuasi Persediaan</label>
                <select
                  className="input"
                  value={form.valuation_method}
                  onChange={(e) => handleChange('valuation_method', e.target.value)}
                >
                  <option value="fifo">FIFO (First In First Out)</option>
                  <option value="lifo">LIFO (Last In First Out)</option>
                  <option value="average">Rata-rata Tertimbang</option>
                </select>
              </div>
            </div>

            <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
              <div>
                <p className="text-sm font-medium text-gray-700 dark:text-gray-200">Izinkan Stok Negatif</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">Memungkinkan transaksi ketika stok tidak mencukupi</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  className="sr-only peer"
                  checked={form.allow_negative_stock}
                  onChange={(e) => handleChange('allow_negative_stock', e.target.checked)}
                />
                <div className="peer h-6 w-11 rounded-full bg-surface-200 transition-colors duration-200 peer-focus-visible:ring-2 peer-focus-visible:ring-[#2563eb] peer-focus-visible:ring-offset-2 peer-focus-visible:ring-offset-white dark:peer-focus-visible:ring-[#60a5fa] dark:peer-focus-visible:ring-offset-surface-950 peer-checked:bg-primary-600 after:absolute after:start-[3px] after:top-[3px] after:h-[18px] after:w-[18px] after:rounded-full after:border after:border-surface-200 after:bg-white after:transition-all after:content-[''] peer-checked:after:translate-x-5 peer-checked:after:border-surface-900 peer-checked:after:bg-surface-900 dark:bg-surface-700"></div>
              </label>
            </div>
          </div>
        </div>

        <div className="flex justify-end mt-6">
          <button type="submit" disabled={saving} className="btn-primary">
            {saving ? (
              <>
                <svg className="animate-spin -ml-1 mr-2 h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Menyimpan...
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" /> Simpan Pengaturan
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
}